import { useEffect, useRef, useState } from "react";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import NumberInput from "@/components/base/NumberInput";
import { Select } from "@/components/base/Field";
import { LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import {
  canTeach,
  formatScore,
  getEvaluationGrades,
  saveEvaluationGrades,
  type Evaluation,
  type EvaluationGrades,
  type GradeStatus,
  type Teachable,
} from "@/services/gradeService";
import { formatDate } from "@/utils/format";

interface Props {
  /** Évaluation ouverte (null : rien n'est affiché). */
  evaluation: Evaluation | null;
  /** Matière affichée. */
  initialSubjectId: string;
  teachable: Teachable | null;
  onClose: () => void;
  onSaved: () => void;
  /** Autre matière choisie : la page la reçoit (elle vérifie les notes non enregistrées). */
  onSubjectChange: (subjectId: string) => void;
  /** Signale des notes tapées mais pas encore enregistrées. */
  onDirtyChange: (dirty: boolean) => void;
}

interface Draft {
  score: string;
  status: GradeStatus;
}

const STATUS_LABEL: Record<GradeStatus, string> = { PRESENT: "Présent", ABSENT: "Absent", EXEMPT: "Dispensé" };

/** Saisie des notes d'une évaluation, matière par matière : une ligne par élève de la classe. */
export default function GradeEntryPanel({ evaluation, initialSubjectId, teachable, onClose, onSaved, onSubjectChange, onDirtyChange }: Props) {
  const { notify } = useToast();
  const panelRef = useRef<HTMLElement>(null);
  const [subjectId, setSubjectId] = useState("");
  const [grid, setGrid] = useState<EvaluationGrades | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (evaluation) setSubjectId(initialSubjectId);
  }, [evaluation, initialSubjectId]);

  // La saisie s'affiche sous la liste des évaluations : on y descend à l'ouverture d'une évaluation.
  const evaluationId = evaluation?.id;
  useEffect(() => {
    if (evaluationId) requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [evaluationId]);

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  useEffect(() => {
    if (!evaluation || !subjectId) return;
    setGrid(null);
    setDirty(false);
    getEvaluationGrades(evaluation.id, subjectId)
      .then((g) => {
        setGrid(g);
        setDrafts(
          Object.fromEntries(
            g.rows.map((r) => [r.enrollmentId, { score: r.score === null ? "" : String(r.score), status: r.status ?? "PRESENT" }]),
          ),
        );
      })
      .catch((err: unknown) => notify(err instanceof Error ? err.message : "Chargement impossible.", "error"));
  }, [evaluation, subjectId, notify]);

  const max = grid?.evaluation.maxScore ?? evaluation?.maxScore ?? 20;
  const editable = grid?.canEdit === true;
  const set = (id: string, patch: Partial<Draft>) => {
    setDirty(true);
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };
  const invalid = Object.values(drafts).some((d) => d.status === "PRESENT" && d.score !== "" && Number(d.score) > max);
  const entered = Object.values(drafts).filter((d) => d.status !== "PRESENT" || d.score !== "").length;
  const subjects = grid?.evaluation.subjects ?? evaluation?.subjects ?? [];

  const save = async (close: boolean) => {
    if (!grid || !evaluation) return;
    if (invalid) return notify(`Une note dépasse la note maximale (${formatScore(max)}).`, "error");
    setSaving(true);
    try {
      const saved = await saveEvaluationGrades(
        evaluation.id,
        subjectId,
        grid.rows.map((r) => {
          const d = drafts[r.enrollmentId];
          return { enrollmentId: r.enrollmentId, status: d.status, score: d.status === "PRESENT" && d.score !== "" ? Number(d.score) : null };
        }),
      );
      setGrid(saved);
      setDirty(false);
      const subjectName = subjects.find((s) => s.subjectId === subjectId)?.name ?? "la matière";
      notify(
        saved.queued
          ? `Hors ligne : notes de ${subjectName} gardées sur l'appareil, envoyées au retour de la connexion.`
          : `Notes de ${subjectName} enregistrées.`,
      );
      onSaved();
      if (close) onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
    } finally {
      setSaving(false);
    }
  };

  const changeSubject = (next: string) => onSubjectChange(next);

  if (!evaluation) return null;

  const close = () => {
    if (dirty && !window.confirm("Les notes saisies ne sont pas enregistrées. Fermer quand même ?")) return;
    onClose();
  };

  return (
    <section ref={panelRef} aria-label={`Saisie des notes — ${evaluation.title}`} className="mt-4 scroll-mt-4 animate-slide-up">
    <Card padded={false}>
      <div className="flex flex-col gap-2 border-b border-background-200 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
            <i className="ri-edit-box-line text-lg"></i>
          </span>
          <div>
            <h3 className="text-base font-semibold font-heading text-foreground-950">Saisie des notes — {evaluation.title}</h3>
            <p className="mt-0.5 text-xs text-foreground-500">
              Du {formatDate(evaluation.date)} · toutes les matières sont notées sur {formatScore(max)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          className="flex h-8 w-8 shrink-0 items-center justify-center self-end rounded-md text-foreground-500 hover:bg-background-200 cursor-pointer sm:self-auto"
          aria-label="Fermer la saisie"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>
      <div className="p-4">
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <Select label="Matière" value={subjectId} onChange={(e) => changeSubject(e.target.value)}>
          {subjects.map((s) => (
            <option key={s.subjectId} value={s.subjectId}>
              {s.name} (coef. {s.coefficient}) — {s.graded}/{evaluation?.studentCount ?? 0}
              {evaluation && !canTeach(teachable, evaluation.classId, s.subjectId) ? " · consultation" : ""}
            </option>
          ))}
        </Select>
        {grid && (
          <p className="pb-2 text-xs text-foreground-500">
            Moyenne de la classe : <strong className="text-foreground-800">{formatScore(grid.average)}</strong>
          </p>
        )}
      </div>

      {!grid ? (
        <LoadingState label="Chargement des élèves..." />
      ) : (
        <>
          {!editable && (
            <p className="mb-3 rounded-md border border-background-200 bg-background-100/60 px-3 py-2 text-xs text-foreground-600">
              {grid.periodLocked
                ? "La période est verrouillée : les notes ne peuvent plus changer."
                : "Consultation seulement : vous ne saisissez que les notes de vos matières."}
            </p>
          )}
          <p className="mb-2 text-xs text-foreground-500">
            {entered} élève{entered > 1 ? "s" : ""} sur {grid.rows.length} renseigné{entered > 1 ? "s" : ""}. Laissez la case vide
            pour un élève pas encore noté ; un absent ou un dispensé ne compte pas dans la moyenne.
          </p>
          <div className="overflow-x-auto rounded-md border border-background-200">
            <table className="w-full text-sm">
              <thead className="bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Élève</th>
                  <th className="px-3 py-2 font-medium">Situation</th>
                  <th className="px-3 py-2 font-medium text-right">Note / {formatScore(max)}</th>
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((r) => {
                  const d = drafts[r.enrollmentId] ?? { score: "", status: "PRESENT" as GradeStatus };
                  const tooHigh = d.status === "PRESENT" && d.score !== "" && Number(d.score) > max;
                  return (
                    <tr key={r.enrollmentId} className="border-t border-background-200">
                      <td className="px-3 py-2">
                        <span className="font-medium text-foreground-900">{r.lastName.toUpperCase()}</span> {r.firstName}
                        <span className="ml-2 text-xs text-foreground-400">{r.matricule}</span>
                      </td>
                      <td className="px-3 py-2">
                        {editable ? (
                          <select
                            value={d.status}
                            onChange={(e) => set(r.enrollmentId, { status: e.target.value as GradeStatus })}
                            aria-label={`Situation de ${r.firstName} ${r.lastName}`}
                            className="rounded-md border border-background-300 bg-background-50 px-2 py-1 text-xs"
                          >
                            {(Object.keys(STATUS_LABEL) as GradeStatus[]).map((s) => (
                              <option key={s} value={s}>
                                {STATUS_LABEL[s]}
                              </option>
                            ))}
                          </select>
                        ) : d.status !== "PRESENT" ? (
                          <Badge tone="neutral">{STATUS_LABEL[d.status]}</Badge>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {editable && d.status === "PRESENT" ? (
                          <NumberInput
                            min={0}
                            step="0.25"
                            value={d.score}
                            onChange={(e) => set(r.enrollmentId, { score: e.target.value })}
                            placeholder="—"
                            aria-label={`Note de ${r.firstName} ${r.lastName}`}
                            aria-invalid={tooHigh}
                            className={`w-24 rounded-md border px-2 py-1 text-right tabular-nums focus:outline-none focus:ring-2 ${
                              tooHigh
                                ? "border-accent-400 bg-accent-50 focus:ring-accent-100"
                                : "border-background-300 bg-background-50 focus:border-primary-400 focus:ring-primary-100"
                            }`}
                          />
                        ) : (
                          <span className="tabular-nums">{d.status === "PRESENT" ? formatScore(d.score === "" ? null : Number(d.score)) : "—"}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {grid.rows.length === 0 && <p className="mt-3 text-sm text-foreground-500">Aucun élève inscrit dans cette classe cette année.</p>}
        </>
      )}
      </div>
      <div className="flex flex-wrap justify-end gap-2 border-t border-background-200 p-4">
        <Button variant="outline" onClick={close} disabled={saving}>
          Fermer
        </Button>
        {editable && (
          <>
            <Button variant="secondary" icon="ri-save-line" onClick={() => save(false)} disabled={saving || invalid || !dirty}>
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
            <Button icon="ri-save-3-line" onClick={() => save(true)} disabled={saving || invalid}>
              Enregistrer et fermer
            </Button>
          </>
        )}
      </div>
    </Card>
    </section>
  );
}
